import { Button, Flex, Input, InputGroup, Link } from '@chakra-ui/react'
import { QRCodeCanvas } from 'qrcode.react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { DetailSheet } from '../../../../components/DetailSheet'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { useAppToast, useHasRole } from '../../../../helpers'
import UrlShortenerService, { IGetUrlShortenerResponse } from '../../../../services/UrlShortener/UrlShortenerService'

export function UtmGeneratorViewModal({ onClose, id }: { onClose: () => void; id: number | null }) {
  const toast = useAppToast()
  const [urlData, setUrlData] = useState<null | IGetUrlShortenerResponse>(null)
  const [fullUrl, setFullUrl] = useState<string>('')
  const hasRole = useHasRole()
  const canEdit = !!urlData && hasRole(urlData.used_by === 'commercial' ? 'edit_url' : 'edit_marketing_url')

  useEffect(() => {
    if (id === null) return

    let isActive = true

    // Limpa antes de buscar: o modal fica montado, e sem isso a ficha do registro anterior
    // aparece por um instante no lugar do esqueleto.
    setUrlData(null)

    UrlShortenerService.get(id).then((data) => {
      // Sem a guarda, a resposta de um registro aberto antes sobrescreveria a ficha do atual — e o
      // `save()` grava em `urlData.id`, ou seja, no UTM errado.
      if (!isActive) return
      setFullUrl(`https://abra.com.br/short/${data.short_path}`)
      setUrlData(data)
    })

    return () => {
      isActive = false
    }
  }, [id])

  const handleDownload = () => {
    const canvas = document.getElementById('abra-qrcode-canvas') as HTMLCanvasElement | null
    if (!canvas) return
    const pngUrl = canvas.toDataURL('image/png')
    const downloadLink = document.createElement('a')
    downloadLink.href = pngUrl
    downloadLink.download = `${urlData?.title || 'qrcode'}.png`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
  }

  const save = async () => {
    if (!urlData || !canEdit) return

    try {
      await UrlShortenerService.update(urlData.id, {
        title: urlData.title,
        original_url: urlData.original_url,
        short_path: urlData.short_path,
        used_by: urlData.used_by
      })

      setUrlData({ ...urlData, title: urlData.title })
      toast.success('Título atualizado com sucesso')
    } catch (error) {
      toast.error('Erro ao atualizar o título', error)
    }
  }

  const utm = (value?: string | null) => value || undefined

  return (
    <FormModal isOpen={id !== null} onClose={onClose} title="Detalhes" size="wide">
      <Flex gap={4} direction={{ base: 'column', sm: 'row' }}>
        <Flex flex={{ base: '1 1 100%', sm: '1 1 58%' }} direction="column" gap={4}>
          {/* O título é o único campo editável da tela, e por isso continua num `FormField` de
              verdade. O resto é leitura, e leitura é `DetailSheet` — o `FormField` sem controle
              não associa nada. */}
          <FormField label="Título">
            <InputGroup size="sm">
              <Input
                value={urlData?.title ?? ''}
                isDisabled={!canEdit}
                onChange={(e) => urlData && setUrlData({ ...urlData, title: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    save()
                  }
                }}
              />
              {canEdit && <ActionButton action="save" onClick={save} size="sm" borderLeftRadius={0} />}
            </InputGroup>
          </FormField>

          {/* Uma coluna e pares empilhados, como os `FormField` de leitura que esta tela usava:
              a URL de destino é longa demais para caber alinhada à direita de um rótulo. */}
          <DetailSheet
            layout="grid"
            columns={1}
            isLoading={!urlData}
            fields={[
              {
                label: 'URL de Destino',
                value: urlData && (
                  <Link href={urlData.original_url} isExternal color="blue.500" wordBreak="break-all">
                    {urlData.original_url}
                  </Link>
                )
              },
              { label: 'UTM_CAMPAIGN', value: utm(urlData?.utm_campaign) },
              { label: 'UTM_SOURCE', value: utm(urlData?.utm_source) },
              { label: 'UTM_MEDIUM', value: utm(urlData?.utm_medium) },
              { label: 'UTM_CONTENT', value: utm(urlData?.utm_content) },
              { label: 'UTM_TERM', value: utm(urlData?.utm_term) }
            ]}
          />
        </Flex>
        <Flex flex={{ base: '1 1 100%', sm: '1 1 42%' }} direction="column" alignItems="center" gap={4}>
          <QRCodeCanvas value={fullUrl} size={220} id="abra-qrcode-canvas" includeMargin={true} />
          <Button colorScheme="magenta" onClick={handleDownload} isDisabled={!urlData}>
            Baixar PNG
          </Button>
        </Flex>
      </Flex>
    </FormModal>
  )
}
